import React from "react";
import { Badge, EmptyState as Empty, TableActionButton } from "@/components/ui";
import { fmt, fmtDate } from "@/lib/formatters";
import { Receipt, Tag, Wallet, CreditCard, Eye, Edit2, UserX, UserCheck } from "lucide-react";

export const ExpenseGrid = ({ expenses, onView, onEdit, onToggleDisable, onDisburse, canDisburseExpenses }) => {
  if (expenses.length === 0) {
    return (
      <div className="py-12">
        <Empty icon="🧾" title="No expenses recorded" desc="Try adjusting your filters or search terms" />
      </div>
    );
  }

  const PM = { 
    cash: { l: "Cash", c: "text-emerald-600 bg-emerald-50", icon: <Wallet size={16} /> }, 
    bank: { l: "Bank", c: "text-blue-600 bg-blue-50", icon: <CreditCard size={16} /> }, 
    upi: { l: "UPI", c: "text-indigo-600 bg-indigo-50", icon: <Receipt size={16} /> }, 
    cheque: { l: "Cheque", c: "text-slate-600 bg-slate-50", icon: <Receipt size={16} /> } 
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pb-8">
      {expenses.map((exp, i) => {
        const isDisabled = exp.status === 'cancelled';
        const isLocked = ['approved', 'partially_approved', 'paid', 'rejected'].includes(exp.status);
        const cannotEdit = isDisabled || isLocked;
        return (
        <div 
          key={exp._id || i} 
          className={`card p-5 hover:shadow-card-md transition-all group border border-slate-100 flex flex-col ${isDisabled ? "opacity-60 grayscale saturate-0" : ""}`}
        >
          {/* Header */}
          <div className="flex items-start justify-between mb-4">
            <div className="min-w-0 pr-2">
              <div className="flex items-center gap-2 mb-1">
                 <span className="text-[10px] font-bold text-brand-orange uppercase tracking-tight italic">
                    {exp.category || "General"}
                 </span>
                 {isDisabled && <span className="text-[9px] font-black bg-rose-50 text-rose-600 px-1.5 py-0.5 rounded uppercase">Disabled</span>}
              </div>
              <h3 className="font-bold text-brand-dark group-hover:text-brand-blue transition-colors leading-snug truncate capitalize">
                {exp.reason || "Expense Statement"}
              </h3>
              <div className="text-[11px] text-slate-500 font-medium truncate mt-0.5">{exp.toWhomPaid || "Vendor Unknown"}</div>
            </div>
            <div className={`shrink-0 p-2 rounded-xl ${PM[exp.paymentMethod]?.c || "bg-slate-50 text-slate-400"}`}>
               {PM[exp.paymentMethod]?.icon || <Tag size={18} />}
            </div>
          </div>

          {/* Details Grid */}
          <div className="grid grid-cols-2 gap-2 mb-4 flex-1">
              <div className="bg-slate-50 rounded-xl p-2.5 border border-slate-100/50">
                 <div className="text-[9px] font-bold text-slate-400 uppercase tracking-tighter mb-0.5 flex items-center gap-1">
                    Date
                 </div>
                 <div className="text-[11px] font-black text-brand-dark">{fmtDate(exp.date)}</div>
              </div>
              <div className="bg-slate-50/50 rounded-xl p-2.5 border border-slate-100/30 flex flex-col justify-center">
                 <div className="flex items-center justify-between mb-1">
                    <span className="text-[9px] font-bold text-slate-500 uppercase tracking-tighter">Req:</span>
                    <span className="text-[11px] font-black text-slate-700">₹{fmt(Math.abs(exp.amount))}</span>
                 </div>
                 {['approved', 'partially_approved', 'owner_approved'].includes(exp.status) && (
                   <div className="flex items-center justify-between" title="Pending for Payment">
                      <div className="flex items-center gap-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse flex-shrink-0"></span>
                        <span className="text-[9px] font-bold text-amber-500 uppercase tracking-tighter">Pending:</span>
                      </div>
                      <span className="text-[11px] font-black text-slate-700">₹{fmt(Math.abs(exp.approvedAmount || exp.amount))}</span>
                   </div>
                 )}
                 {exp.status === 'paid' && (
                   <div className="flex items-center justify-between">
                      <span className="text-[9px] font-bold text-emerald-600 uppercase tracking-tighter">Paid:</span>
                      <span className="text-[11px] font-black text-emerald-600">₹{fmt(Math.abs(exp.approvedAmount || exp.amount))}</span>
                   </div>
                 )}
              </div>
              <div className="bg-slate-50 rounded-xl p-2.5 border border-slate-100/50">
                 <div className="text-[9px] font-bold text-slate-400 uppercase tracking-tighter mb-0.5 flex items-center gap-1">
                    Requester
                 </div>
                 <div className="text-[11px] font-bold text-slate-600 truncate uppercase">{exp.byWhomPaid || "Staff"}</div>
              </div>
              <div className="bg-slate-50 rounded-xl p-2.5 border border-slate-100/50">
                 <div className="text-[9px] font-bold text-slate-400 uppercase tracking-tighter mb-0.5">
                    ID
                 </div>
                 <div className="text-[10px] font-bold text-brand-orange truncate">#{exp.expenseId || "—"}</div>
              </div>
          </div>

          {/* Footer Actions */}
          <div className="flex items-center justify-between pt-4 border-t border-slate-50">
             <Badge status={exp.status || "pending"} />
             
             <div className="flex items-center gap-1.5">
                <button 
                  onClick={() => onView(exp)}
                  className="p-1.5 text-slate-400 hover:text-brand-blue hover:bg-blue-50 rounded-lg transition-all" 
                  title="View Detail"
                >
                  <Eye size={16}/>
                </button>
                <button 
                  disabled={cannotEdit}
                  onClick={() => onEdit(exp)}
                  className={`p-1.5 text-slate-400 hover:text-emerald-500 hover:bg-emerald-50 rounded-lg transition-all ${cannotEdit ? "opacity-30 cursor-not-allowed" : ""}`}
                  title={isLocked ? "Record is finalized and cannot be edited" : "Edit Record"}
                >
                  <Edit2 size={16}/>
                </button>
                {canDisburseExpenses && ['approved', 'partially_approved'].includes(exp.status) && (
                  <button
                    onClick={() => onDisburse(exp)}
                    className="p-1.5 text-slate-400 hover:text-brand-orange hover:bg-orange-50 rounded-lg transition-all"
                    title="Pay / Disburse Amount"
                  >
                    <CreditCard size={16} />
                  </button>
                )}
             </div>
          </div>
        </div>
        );
      })}
    </div>
  );
};




